import React, { useState } from "react";
import { caseStudies } from "../utils/mockData";
import { ArrowRight, X, ExternalLink, BarChart, Tag, Image as ImageIcon } from "lucide-react";
import * as Dialog from "@radix-ui/react-dialog";
import useIntersectionObserver from "../hooks/useIntersectionObserver";

export const CaseStudyGallerySection = () => {
  const [activeFilter, setActiveFilter] = useState("Semua");
  const [selectedCaseStudy, setSelectedCaseStudy] = useState(null);
  const [galleryRef, galleryInView] = useIntersectionObserver();

  // Extract unique categories dynamically based on the updated data
  const categories = ["Semua", ...new Set(caseStudies.map((item) => item.category))];

  // Filter items without syntax error
  const filteredCaseStudies = activeFilter === "Semua"
    ? caseStudies
    : caseStudies.filter((item) => item.category === activeFilter);

  // Helper to handle image fallback
  const handleImageError = (e) => {
    e.target.src = `https://placehold.co/600x600/1a1a1a/FFF?text=Image+Not+Found`;
  };

  return (
    <section id="portfolio" className="py-20 bg-gray-50 dark:bg-[#121212]">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header & Filter Section */}
        <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-8">
          <div className="max-w-2xl">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              Studi Kasus & Hasil Nyata
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              Lihat bagaimana kami membantu klien mengatasi tantangan visual dan mencapai pertumbuhan digital yang signifikan.
            </p>
          </div>

          {/* Filter Pills (Scrollable on small screens) */}
          <div 
            className="flex overflow-x-auto pb-2 md:pb-0 md:flex-wrap gap-2 w-full md:w-auto hide-scrollbar" 
            role="tablist" 
            aria-label="Filter portofolio"
          >
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveFilter(category)}
                role="tab"
                aria-selected={activeFilter === category}
                className={`whitespace-nowrap px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                  activeFilter === category
                    ? "bg-[#D32F2F] text-white shadow-lg scale-105"
                    : "bg-white dark:bg-[#1a1a1a] text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-[#2a2a2a] border border-gray-200 dark:border-gray-800"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Gallery Grid */}
        <div 
          ref={galleryRef}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {filteredCaseStudies.map((item, index) => (
            <button
              key={item.id}
              onClick={() => setSelectedCaseStudy(item)}
              className={`group text-left relative bg-white dark:bg-[#1a1a1a] rounded-2xl overflow-hidden cursor-pointer shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-2 flex flex-col ${
                galleryInView ? "animate-scale-in opacity-100" : "opacity-0"
              }`}
              style={{ animationDelay: `${index * 100}ms` }}
              aria-label={`Lihat detail studi kasus ${item.title}`}
            >
              {/* Thumbnail Image */}
              <div className="aspect-square w-full relative overflow-hidden bg-gray-200 dark:bg-gray-800">
                <img 
                  src={item.thumbnail} 
                  alt={item.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  onError={handleImageError}
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-80 transition-opacity duration-300" />
                
                {/* Category Badge */}
                <span className="absolute top-4 left-4 bg-white/90 dark:bg-black/80 backdrop-blur-sm px-3 py-1 rounded-lg text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider shadow-sm">
                  {item.categorySlug === 'social-media' ? 'Feed Management' : 'Branding'}
                </span>

                {/* Client Name on Hover */}
                <span className="absolute bottom-4 left-4 text-white font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300 translate-y-2 group-hover:translate-y-0">
                  Client: {item.client}
                </span>
              </div>

              {/* Card Content */}
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-[#D32F2F] transition-colors line-clamp-2">
                  {item.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-2 mb-6 flex-grow">
                  {item.challenge}
                </p>
                
                {/* Results Preview */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-800 mt-auto">
                  <div className="flex gap-4">
                    {item.results.slice(0, 2).map((res, idx) => (
                      <div key={idx}>
                        <p className="text-[10px] text-gray-500 dark:text-gray-500 font-medium uppercase tracking-wider">{res.label}</p>
                        <p className="text-sm font-bold text-[#D32F2F]">{res.value}</p>
                      </div>
                    ))}
                  </div>
                  <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-[#2a2a2a] flex items-center justify-center group-hover:bg-[#D32F2F] group-hover:text-white transition-colors">
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Global Modal (Rendered only once, controlled by selectedCaseStudy) */}
        <Dialog.Root open={!!selectedCaseStudy} onOpenChange={(isOpen) => !isOpen && setSelectedCaseStudy(null)}>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6 data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out">
              {selectedCaseStudy && (
                <Dialog.Content 
                  className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-2xl outline-none data-[state=open]:animate-scale-in custom-scrollbar flex flex-col"
                  aria-describedby={`desc-${selectedCaseStudy.id}`}
                >
                  <Dialog.Title className="sr-only">{selectedCaseStudy.title}</Dialog.Title>
                  <div id={`desc-${selectedCaseStudy.id}`} className="sr-only">Detail portofolio untuk {selectedCaseStudy.client}</div>
                  
                  {/* Modal Header Image */}
                  <div className="relative h-64 sm:h-80 w-full shrink-0">
                    <img 
                      src={selectedCaseStudy.thumbnail} 
                      alt={selectedCaseStudy.title}
                      className="w-full h-full object-cover"
                      onError={handleImageError}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
                    
                    <Dialog.Close asChild>
                      <button 
                        className="absolute top-4 right-4 z-10 bg-black/30 hover:bg-[#D32F2F] text-white p-2.5 rounded-full backdrop-blur-md transition-all duration-300 focus:ring-2 focus:ring-white/50"
                        aria-label="Tutup modal"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </Dialog.Close>
                    
                    <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
                      <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-3 shadow-sm leading-tight text-balance">
                        {selectedCaseStudy.title}
                      </h2>
                      <div className="flex flex-wrap gap-3 items-center text-white/90">
                        <span className="font-medium bg-white/20 px-4 py-1.5 rounded-full backdrop-blur-sm text-sm">
                           Client: {selectedCaseStudy.client}
                        </span>
                        <span className="text-sm border border-white/30 px-3 py-1.5 rounded-full text-white/90 bg-black/20 backdrop-blur-sm">
                          {selectedCaseStudy.category}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Modal Body */}
                  <div className="p-6 sm:p-8 space-y-10">
                    
                    {/* Results / Metrics Grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-auto-fit min-[400px]:grid-cols-2 md:flex md:flex-wrap gap-4 items-center justify-start">
                      {selectedCaseStudy.results.map((res, idx) => (
                        <div 
                          key={idx} 
                          className="flex-1 min-w-[140px] text-center p-4 bg-gray-50 dark:bg-[#0f0f0f] rounded-xl border border-gray-100 dark:border-gray-800 hover:border-[#D32F2F]/30 transition-colors"
                        >
                          <p className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-widest mb-1.5 font-bold">{res.label}</p>
                          <p className="text-xl sm:text-2xl font-black text-[#D32F2F]">{res.value}</p>
                        </div>
                      ))}
                    </div>

                    {/* Challenge & Solution */}
                    <div className="grid md:grid-cols-2 gap-8">
                      <div className="bg-red-50/50 dark:bg-red-900/5 p-6 rounded-2xl border border-red-100 dark:border-red-900/20">
                        <h4 className="flex items-center gap-3 text-lg font-bold text-gray-900 dark:text-white mb-4">
                          <div className="p-2.5 bg-white dark:bg-[#1a1a1a] rounded-xl text-[#D32F2F] shadow-sm">
                            <BarChart className="w-5 h-5" />
                          </div>
                          Tantangan Visual
                        </h4>
                        <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-base">
                          {selectedCaseStudy.challenge}
                        </p>
                      </div>
                      <div className="bg-blue-50/50 dark:bg-blue-900/5 p-6 rounded-2xl border border-blue-100 dark:border-blue-900/20">
                        <h4 className="flex items-center gap-3 text-lg font-bold text-gray-900 dark:text-white mb-4">
                          <div className="p-2.5 bg-white dark:bg-[#1a1a1a] rounded-xl text-blue-600 dark:text-blue-400 shadow-sm">
                            <ExternalLink className="w-5 h-5" />
                          </div>
                          Solusi & Eksekusi
                        </h4>
                        <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-base">
                          {selectedCaseStudy.solution}
                        </p>
                      </div>
                    </div>
                    
                    {/* Gallery Grid in Modal */}
                    {selectedCaseStudy.gallery && selectedCaseStudy.gallery.length > 0 && (
                      <div>
                        <h4 className="flex items-center gap-3 text-xl font-bold text-gray-900 dark:text-white mb-6">
                          <div className="p-2.5 bg-purple-50 dark:bg-purple-900/20 rounded-xl text-purple-600 dark:text-purple-400">
                            <ImageIcon className="w-5 h-5" />
                          </div>
                          Galeri Project
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                          {selectedCaseStudy.gallery.map((imgSrc, imgIdx) => (
                            <div key={imgIdx} className="group relative rounded-2xl overflow-hidden bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 aspect-video sm:aspect-square md:aspect-video cursor-zoom-in">
                              <img 
                                src={imgSrc} 
                                alt={`Hasil karya ${imgIdx + 1} untuk ${selectedCaseStudy.client}`} 
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                onError={handleImageError}
                                loading="lazy"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Tags */}
                    <div className="pt-8 border-t border-gray-100 dark:border-gray-800">
                      <h4 className="flex items-center gap-2 text-sm font-bold text-gray-900 dark:text-white mb-4">
                        <Tag className="w-4 h-4 text-gray-400" />
                        Skills & Tags
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedCaseStudy.tags.map(tag => (
                          <span key={tag} className="px-4 py-2 bg-gray-100 dark:bg-[#2a2a2a] text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-200 dark:hover:bg-[#333] transition-colors cursor-default">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                </Dialog.Content>
              )}
            </Dialog.Overlay>
          </Dialog.Portal>
        </Dialog.Root>

      </div>
    </section>
  );
};